import React, { useState, useEffect } from "react";
import AdminModal from "../../Common/AdminModal";

const SUBJECTS = [
  "Polity",
  "History",
  "Geography",
  "Economy",
  "Environment",
  "Science & Tech",
  "Current Affairs",
  "Ethics",
];

export default function QuestionEditorModal({
  isOpen,
  onClose,
  onSave,
  initialData = null,
}) {
  const isEditing = Boolean(initialData && initialData.id);

  const [formData, setFormData] = useState({
    subject: "Polity",
    topic: "",
    subtopic: "",
    question: "",
    options: {
      A: "",
      B: "",
      C: "",
      D: "",
    },
    correct_answer: "A",
    explanation: "",
    difficulty: "Medium",
    pyq_year: "",
    marks: 2,
    negative_marks: 0.66,
    change_reason: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        subject: initialData.subject || "Polity",
        topic: initialData.topic || "",
        subtopic: initialData.subtopic || "",
        question: initialData.question || "",
        options: {
          A: initialData.options?.A || "",
          B: initialData.options?.B || "",
          C: initialData.options?.C || "",
          D: initialData.options?.D || "",
        },
        correct_answer: initialData.correct_answer || "A",
        explanation: initialData.explanation || "",
        difficulty: initialData.difficulty || "Medium",
        pyq_year: initialData.pyq_year || "",
        marks: initialData.marks || 2,
        negative_marks: initialData.negative_marks || 0.66,
        change_reason: "",
      });
    } else {
      setFormData({
        subject: "Polity",
        topic: "",
        subtopic: "",
        question: "",
        options: { A: "", B: "", C: "", D: "" },
        correct_answer: "A",
        explanation: "",
        difficulty: "Medium",
        pyq_year: "",
        marks: 2,
        negative_marks: 0.66,
        change_reason: "",
      });
    }
    setErrors({});
  }, [initialData, isOpen]);

  const validate = () => {
    const errs = {};
    if (!formData.topic.trim()) errs.topic = "Topic is required";
    if (!formData.question.trim()) errs.question = "Question text is required";
    if (!formData.options.A.trim()) errs.option_A = "Option A is required";
    if (!formData.options.B.trim()) errs.option_B = "Option B is required";
    if (!formData.options.C.trim()) errs.option_C = "Option C is required";
    if (!formData.options.D.trim()) errs.option_D = "Option D is required";
    if (!formData.explanation.trim()) errs.explanation = "Explanation is required";
    if (isEditing && !formData.change_reason.trim()) {
      errs.change_reason = "Please state the reason for this correction/edit (for version history)";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(formData);
    onClose();
  };

  return (
    <AdminModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit Question: ${initialData.id} (v${initialData.version || 1})` : "Create New Question"}
      maxWidth="780px"
      footer={
        <>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            onClick={handleSubmit}
          >
            {isEditing ? "Save Correction (New Version)" : "Add Question"}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="admin-form-row">
          <div className="admin-form-group">
            <label className="admin-form-label">Subject *</label>
            <select
              className="admin-form-select"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            >
              {SUBJECTS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Topic *</label>
            <input
              type="text"
              className="admin-form-input"
              placeholder="e.g. Fundamental Rights, Preamble"
              value={formData.topic}
              onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
            />
            {errors.topic && <span style={{ color: "#ef4444", fontSize: "12px" }}>{errors.topic}</span>}
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Subtopic (Optional)</label>
            <input
              type="text"
              className="admin-form-input"
              placeholder="e.g. Article 21, Writs"
              value={formData.subtopic}
              onChange={(e) => setFormData({ ...formData, subtopic: e.target.value })}
            />
          </div>
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Question Text *</label>
          <textarea
            className="admin-form-textarea"
            rows={3}
            placeholder="Enter the full question statement..."
            value={formData.question}
            onChange={(e) => setFormData({ ...formData, question: e.target.value })}
          />
          {errors.question && <span style={{ color: "#ef4444", fontSize: "12px" }}>{errors.question}</span>}
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label className="admin-form-label">Options (MCQ) *</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            {["A", "B", "C", "D"].map((optKey) => (
              <div key={optKey}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <span
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: formData.correct_answer === optKey ? "#dcfce7" : "#e2e8f0",
                      color: formData.correct_answer === optKey ? "#16a34a" : "#475569",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                      fontWeight: 700,
                    }}
                  >
                    {optKey}
                  </span>
                  <input
                    type="text"
                    className="admin-form-input"
                    placeholder={`Option ${optKey}`}
                    value={formData.options[optKey]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        options: { ...formData.options, [optKey]: e.target.value },
                      })
                    }
                  />
                </div>
                {errors[`option_${optKey}`] && (
                  <span style={{ color: "#ef4444", fontSize: "11px", marginLeft: "30px" }}>
                    {errors[`option_${optKey}`]}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label className="admin-form-label">Correct Answer *</label>
            <select
              className="admin-form-select"
              value={formData.correct_answer}
              onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
            >
              <option value="A">Option A</option>
              <option value="B">Option B</option>
              <option value="C">Option C</option>
              <option value="D">Option D</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Difficulty</label>
            <select
              className="admin-form-select"
              value={formData.difficulty}
              onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
            >
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">PYQ Year (Optional)</label>
            <input
              type="text"
              className="admin-form-input"
              placeholder="e.g. 2022"
              value={formData.pyq_year}
              onChange={(e) => setFormData({ ...formData, pyq_year: e.target.value })}
            />
          </div>
        </div>

        <div className="admin-form-row">
          <div className="admin-form-group">
            <label className="admin-form-label">Marks</label>
            <input
              type="number"
              step="0.5"
              className="admin-form-input"
              value={formData.marks}
              onChange={(e) => setFormData({ ...formData, marks: parseFloat(e.target.value) || 2 })}
            />
          </div>

          <div className="admin-form-group">
            <label className="admin-form-label">Negative Marks</label>
            <input
              type="number"
              step="0.01"
              className="admin-form-input"
              value={formData.negative_marks}
              onChange={(e) => setFormData({ ...formData, negative_marks: parseFloat(e.target.value) || 0.66 })}
            />
          </div>
        </div>

        <div className="admin-form-group">
          <label className="admin-form-label">Explanation / Solution *</label>
          <textarea
            className="admin-form-textarea"
            rows={3}
            placeholder="Detailed explanation justifying the correct answer..."
            value={formData.explanation}
            onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
          />
          {errors.explanation && <span style={{ color: "#ef4444", fontSize: "12px" }}>{errors.explanation}</span>}
        </div>

        {isEditing && (
          <div
            className="admin-form-group"
            style={{
              background: "#fef2f2",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid #fecaca",
            }}
          >
            <label className="admin-form-label" style={{ color: "#b91c1c" }}>
              Reason for Correction (Audited Version History) *
            </label>
            <input
              type="text"
              className="admin-form-input"
              placeholder="e.g. Updated key based on official UPSC answer key, fixed typo in explanation"
              value={formData.change_reason}
              onChange={(e) => setFormData({ ...formData, change_reason: e.target.value })}
            />
            {errors.change_reason && (
              <span style={{ color: "#ef4444", fontSize: "12px" }}>{errors.change_reason}</span>
            )}
          </div>
        )}
      </form>
    </AdminModal>
  );
}
